import React, { useCallback, useState } from "react";

// icons
import { FiSearch } from "react-icons/fi";
import { FaFilePdf } from "react-icons/fa6";
import {
  MdSearch,
  MdVisibility,
  MdSwapVert,
  MdArrowUpward,
  MdArrowDownward,
} from "react-icons/md";
import Card from "../../../../components/card";
import ModalViewDetailHours from "./ModalViewDetailHours";
import SkeletonTable from "../../../../components/skeleton/table";
import api from "../../../../services/axios";
import { Toggle } from "rsuite";
import { toast } from "react-toastify";
import ModalDetailPdf from "./ModalDetailPdf";

interface TableColumn {
  key: string;
  header: string;
}

interface TableRow {
  [key: string]: string | number;
}

interface ResponsiveTableProps {
  columns: TableColumn[];
  data: TableRow[];
}

const MONTH_LABELS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const selectCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const labelCls = "text-sm font-semibold text-gray-700 dark:text-gray-300";

const HoursManagementTable: React.FC<ResponsiveTableProps> = ({
  columns,
  data,
}) => {
  const [hourData, setHourData] = useState<any>(data);
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [sortedColumn, setSortedColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [openHoursDetailModal, setHoursDetailModal] = useState<boolean>(false);
  const [hoursDetail, setHoursDetail] = useState<object | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [checkedLoading, setCheckedLoading] = useState<boolean>(false);
  const [openPdfViewerModal, setPdfViewerModal] = useState<boolean>(false);

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);

  const handleSort = (columnKey: string) => {
    setSortedColumn(columnKey);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredData = hourData.filter((row) =>
    Object.values(row).some((value) =>
      value
        ? value.toString().toLowerCase().includes(searchTerm?.toLowerCase())
        : ""
    )
  );

  const sortedData = sortedColumn
    ? [...filteredData].sort((a, b) =>
        sortOrder === "asc"
          ? a[sortedColumn] > b[sortedColumn]
            ? 1
            : -1
          : a[sortedColumn] < b[sortedColumn]
          ? 1
          : -1
      )
    : filteredData;

  const handleRowClick = (row: TableRow) => {
    // console.log("Row Clicked:", row);
    // Add your custom action here
    setHoursDetail(row);
    console.log('rowClick', hoursDetail)
    setHoursDetailModal(true);
  };

  const handleRowClickReport = (row: TableRow) => {
    setLoadingReport(true);
    setTimeout(() => {
      setHoursDetail(row);
      console.log('clickReport', hoursDetail)
      setPdfViewerModal(true);

      setLoadingReport(false);
    }, 500);
  };

  const handleSetYear = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(event.target.value));
  };

  const handleSetMonth = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonth(parseInt(event.target.value));
  };

  const onSearch = async () => {
    setLoading(true);
    api
      .get(
        `TimeManagement/GetAdminWorkedHours?month=${selectedMonth}&year=${selectedYear}`
      )
      .then((response) => {
        setHourData(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("There was an error!", error);
        setLoading(true);
      });
  };

  const onToggle = useCallback(async (row: TableRow) => {
    setCheckedLoading(true);
    if (row.status === "Aprovada") {
      patchStatusTimeSheet(row.timeSheetId, 2); // em analise
      setTimeout(() => {
        onSearch();
      }, 1000);
    } else {
      patchStatusTimeSheet(row.timeSheetId, 3); // aprovado
      setTimeout(() => {
        onSearch();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function patchStatusTimeSheet(timeSheetId: any, status: number) {
    try {
      const response = await api.patch(
        `/TimeManagement/ChangeStatus?timesheetId=${timeSheetId}&status=${status}`
      );

      // console.log(JSON.stringify(status));

      if (response.status !== 200) {
        let errorMessage =
          response.data.message || JSON.stringify(response.data);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        setCheckedLoading(false);
      }

      toast.success("Status atualizado com sucesso", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setCheckedLoading(false);
      return response.data;
    } catch (error) {
      console.error("Error updating worked status:", error);
      toast.error("Erro ao atualizar o status", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setCheckedLoading(false);
    }
  }

  const sortIcon = (columnKey: string) => {
    if (sortedColumn !== columnKey)
      return (
        <MdSwapVert
          className="h-3.5 w-3.5 text-gray-400 opacity-0 transition-opacity group-hover/th:opacity-100"
          aria-hidden="true"
        />
      );
    return sortOrder === "asc" ? (
      <MdArrowUpward className="h-3.5 w-3.5 text-dv-blue-600 dark:text-dv-cyan-300" aria-hidden="true" />
    ) : (
      <MdArrowDownward className="h-3.5 w-3.5 text-dv-blue-600 dark:text-dv-cyan-300" aria-hidden="true" />
    );
  };

  return (
    <div className="flex flex-col gap-5">
      {/* ── Toolbar de filtros ─────────────────────────────────────────── */}
      <Card extra="p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="year" className={labelCls}>
              Ano
            </label>
            <select
              className={selectCls}
              id="year"
              onChange={handleSetYear}
              value={selectedYear}
            >
              {[2023, 2024, 2025, 2026].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="month" className={labelCls}>
              Mês
            </label>
            <select
              className={selectCls}
              id="month"
              value={selectedMonth}
              onChange={handleSetMonth}
            >
              {MONTH_LABELS.map((m, i) => (
                <option key={i + 1} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-2.5 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1.5 sm:max-w-xs sm:flex-1">
            <label htmlFor="search-hours" className={labelCls}>
              Buscar
            </label>
            <div className="relative">
              <FiSearch
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                id="search-hours"
                type="text"
                placeholder="Buscar por colaborador, cliente, projeto…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={onSearch}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl bg-ink-900 px-6 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            <MdSearch className="h-4 w-4" aria-hidden="true" />
            {loading ? "Processando…" : "Buscar"}
          </button>
        </div>
      </Card>

      {/* ── Tabela ─────────────────────────────────────────────────────── */}
      {loading ? (
        <SkeletonTable active={loading} />
      ) : (
        <Card extra="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-bold text-navy-700 dark:text-white">
              Apontamentos
            </h3>
            <span className="rounded-full bg-dv-blue-50 px-2.5 py-1 text-[11px] font-semibold text-dv-blue-600 dark:bg-dv-blue-500/15 dark:text-dv-cyan-300">
              {sortedData.length} registro{sortedData.length === 1 ? "" : "s"}
            </span>
          </div>

          {sortedData.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-700 dark:border-gray-700 dark:bg-navy-800 dark:text-gray-300">
              Nenhuma hora apontada para este período.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800 dark:shadow-none">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-navy-900">
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        scope="col"
                        className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(column.key)}
                          className="group/th flex items-center gap-1.5 uppercase transition-colors hover:text-dv-blue-600 dark:hover:text-dv-cyan-300"
                        >
                          {column.header}
                          {sortIcon(column.key)}
                        </button>
                      </th>
                    ))}
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                    >
                      Relatório
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                    >
                      Detalhes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={`px-4 py-3 ${
                            column.key === "totalHours"
                              ? "font-semibold tabular-nums text-navy-700 dark:text-white"
                              : column.key === "nmEmployee"
                              ? "font-semibold text-navy-700 dark:text-white"
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          <span
                            className="block max-w-[220px] truncate"
                            title={
                              row[column.key] != null
                                ? String(row[column.key])
                                : undefined
                            }
                          >
                            {row[column.key]}
                          </span>
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <Toggle
                          loading={checkedLoading}
                          checked={row.status === "Aprovada" ? true : false}
                          checkedChildren="Aprovada"
                          unCheckedChildren="Pendente"
                          size="lg"
                          onChange={() => onToggle(row)}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          aria-label="Gerar relatório PDF"
                          title="Gerar relatório PDF"
                          disabled={loadingReport}
                          onClick={() => handleRowClickReport(row)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-dv-blue-50 text-dv-blue-600 transition-all hover:bg-dv-blue-500 hover:text-white active:scale-[.95] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-dv-blue-500/15 dark:text-dv-cyan-300 dark:hover:bg-dv-blue-500 dark:hover:text-white"
                        >
                          {loadingReport ? (
                            <span className="text-xs font-bold">…</span>
                          ) : (
                            <FaFilePdf className="h-4 w-4" aria-hidden="true" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => handleRowClick(row)}
                          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3.5 py-2 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 hover:text-dv-blue-600 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-900 dark:hover:text-dv-cyan-300"
                        >
                          <MdVisibility className="h-4 w-4" aria-hidden="true" />
                          Ver detalhes
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <ModalDetailPdf
        openPdfViewerModal={openPdfViewerModal}
        setPdfViewerModal={setPdfViewerModal}
        details={hoursDetail}
      ></ModalDetailPdf>

      <ModalViewDetailHours
        openHoursDetailModal={openHoursDetailModal}
        setOpenHoursDetailModal={setHoursDetailModal}
        details={hoursDetail}
      ></ModalViewDetailHours>
    </div>
  );
};

export default HoursManagementTable;
